import React, {useState, useEffect} from 'react';
import {Card, Grid, useTheme} from '@mui/material';
import {Controller, useForm} from 'react-hook-form';
import {useAppContext} from '@context/AppContext';
import GrowthChartComponent from './GrowthChartComponent';
import SelectInput from '@components/form/select-input/SelectInput';
import {FormControlDiv} from '@modules/home/main/modules-menu/module-search-filter/ModuleSearchFilterStyle';
import TabsComponent from '@modules/home/main/sub-modules-menu/tabsComponent/TabsComponent';
import {getPageData} from '@shared/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';
import FormConstants from '@shared/constants/FormConstants';
import {PulseLoader} from 'react-spinners';
import {CircularProgressDiv} from '@modules/public/main/login/LoginStyles';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {useSnackbar} from 'notistack';
import {useModuleContext} from '@context/ModuleContext';

interface GrowthChartWrapperProps {
  data: any;
  sectionUIData: any;
  sectionRowsData: any;
  onRowClick?: any;
  module?: any;
  sub_module?: any;
  selectedRowDetails?: any;
  recordId?: any;
  handleCheckBoxClick?: any;
  clearSelectedDataCount?: any;
  isFromModules?: boolean;
  rowsCount?: number;
  searchFormFieldsData?: any;
  setSelectedReportsData?: any;
  // eslint-disable-next-line react/boolean-prop-naming
  handleDisableCheckBox?: boolean;
  legacyApp?: string;
  BenefitsPayload?: any;
  isReportsButtonLoader?: boolean;
  onCellClick?: any;
  ParentLegacyValue?: string;
  handleDeleteLegalHold?: (data: any) => void;
  handleEditUser?: (data: any) => void;
  handleDeleteUser?: (data: any) => void;
  // eslint-disable-next-line react/boolean-prop-naming
  apiLoader?: boolean;
}

const GrowthChartWrapper = (props: GrowthChartWrapperProps): any => {
  const theme = useTheme();
  const {enqueueSnackbar} = useSnackbar();
  const {
    sectionUIData,
    module,
    sub_module,
    recordId,
    legacyApp,
    sectionRowsData
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isTabsLoading, setIsTabsLoading] = useState(false);
  const [isSelectLoading, setIsSelectLoading] = useState(true);
  const [dataGridSections, setDataGridSections] = useState([]);
  const [growthChartType, setGrowthChartType] = useState('');
  const [chartData, setChartData] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>('height');
  const [sourceSystemOptions, setSourceSystemOptions] = useState<any[]>([]);

  const [selectedLegacyApp, setSelectedLegacyApp] = useState('');

  const selectedSection = sectionUIData?.sub_sections?.find(
    (section: any) => section[growthChartType?.toLowerCase()]
  );

  const {
    appState: {applicationName}
  } = useAppContext();

  const {
    moduleState: {isBH, isBHReason}
  } = useModuleContext();

  const {control} = useForm({
    mode: 'onChange'
  });
  const fetchSourceSystemOptions = async () => {
    try {
      setIsSelectLoading(true);
      const fieldConfig = sectionUIData?.fields?.source_system;
      if (!fieldConfig?.sql_data) return;

      const payload = {
        sql_data: fieldConfig.sql_data,
        user_id: recordId,
        application_name: applicationName
      };

      const encryptRequestData = EncryptUtils.encryptedData(payload);
      const res = await getPageData(applicationName, module, sub_module, {
        tk: encryptRequestData
      });
      setSourceSystemOptions(res?.data || []);
      // setSelectedLegacyApp(res?.data?.[0]?.GrowthChartGroup || '');
      setIsSelectLoading(false);
    } catch (error) {
      console.error('Source system fetch error', error);
    }
  };

  useEffect(() => {
    fetchSourceSystemOptions();
  }, [recordId, sectionUIData]);

  const handleSelectChange = (event: any, inputName: string) => {
    // setGrowthChartType(event?.target?.value);
    const value = event?.target?.value;
    if (inputName === 'source_system') {
      setSelectedLegacyApp(value);
    } else {
      setGrowthChartType(value);
    }
    setIsTabsLoading(true);
    setIsLoading(true);
    setActiveTab('height');
    setChartData([]);
    setDataGridSections([]);
  };

  const renderSelectFields = (
    inputName: string,
    item: any
  ): React.ReactElement => {
    // const options = [
    //   {value: 'CDC', label: 'CDC'},
    //   {value: 'WHO', label: 'WHO'}
    // ];

    const dropdownOptions =
      inputName === 'source_system' ? sourceSystemOptions : item.options;
    return (
      <Controller
        name={inputName}
        control={control}
        defaultValue={''}
        render={({field: {value, onChange}}) => {
           const isDisabled = inputName === 'growthChart' &&!selectedLegacyApp;
          return (
            <FormControlDiv>
              <SelectInput
                enableDefaultOption
                options={dropdownOptions}
                label={item?.heading || 'Select Option'}
                placeholder={item.placeholder}
                name={inputName}
                value={value}
                fullWidth
                onChange={(e: any) => {
                  onChange(e);
                  handleSelectChange(e, inputName);
                }}
                optionValueKey={item.value_key}
                optionLabelKey={item.label_key}
                 disabled={isDisabled}
              />
            </FormControlDiv>
          );
        }}
      />
    );
  };

  useEffect(() => {
    if (selectedSection && selectedLegacyApp && growthChartType) {
      const sectionData = selectedSection[growthChartType?.toLowerCase()];

      if (sectionData?.sub_sections?.length && growthChartType?.length) {
        const updatedObj: any = sectionData?.sub_sections?.map(
          (item: any) => item
        );
        const tempObj: any = {
          ...sectionData,
          sub_sections: updatedObj
        };
        setDataGridSections({...tempObj});
      }
    }
  }, [growthChartType, selectedSection, selectedLegacyApp, activeTab]);

  const handleActiveTabChange = async (tabName: string) => {
    setActiveTab(tabName);
  };

  const getChartDataByTabName = async (
    tabName: string,
    recordID: string
    // selectedGrowthChart: string
  ) => {
    try {
      const sectionData = selectedSection[growthChartType?.toLowerCase()];
      const subSection = sectionData?.sub_sections?.find(
        (sub: {name: string}) => sub.name === tabName
      );
      const data: any = {
        sql_data: {
          ...subSection?.sql_chart_data
        },
        user_id: recordID,
        parentLegacyApp: selectedLegacyApp,
        isBH_selected: isBH,
        isBH_reason: isBHReason,
        application_name: applicationName
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      const res = await getPageData(
        applicationName,
        props?.module,
        props?.sub_module,
        {
          tk: encryptRequestData
        }
      );
      return res?.data || [];
    } catch (error) {
      const errMessage = getErrorMessage(error);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (selectedLegacyApp && selectedSection) {
        setIsLoading(true);
        const data = await getChartDataByTabName(activeTab, recordId);
        setChartData(data);
        setIsLoading(false);
        setIsTabsLoading(false);
      }
    };
    fetchData();
    handleActiveTabChange(activeTab);
  }, [
    activeTab,
    recordId,
    growthChartType,
    selectedLegacyApp,
    selectedSection
  ]);

  const renderTabs = () => {
    if (dataGridSections && growthChartType?.length) {
      return (
        <TabsComponent
          sectionUIData={dataGridSections}
          sectionRowsData={sectionRowsData}
          handleActiveTabChange={handleActiveTabChange}
          growthChartType={growthChartType}
          module={module}
          sub_module={sub_module}
          legacyApp={legacyApp}
        />
      );
    }
  };
 const areBothDropdownsSelected = selectedLegacyApp && growthChartType;
  return (
    <div style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
      {isSelectLoading ? (
        <CircularProgressDiv height="50vh">
          <PulseLoader size={12} color={theme.palette.primary.main} />
        </CircularProgressDiv>
      ) : (
        <>
          <Grid container spacing={4} sx={{padding: '20px'}}>
            {sectionUIData?.is_select_included &&
              Object.entries(sectionUIData?.fields).map(
                (item: any, index: number) => {
                  return (
                    <Grid
                      item
                      key={index}
                      // xs={6}
                      xs={12}
                      md={3}>
                      {item[1].type === FormConstants.FIELD_TYPES.SELECT
                        ? renderSelectFields(item[0], item[1])
                        : ''}
                    </Grid>
                  );
                }
              )}
          </Grid>

        {areBothDropdownsSelected? (isTabsLoading ? (
            <CircularProgressDiv height="50vh">
              <PulseLoader size={12} color={theme.palette.primary.main} />
            </CircularProgressDiv>
          ) : (
            <div style={{display: 'flex', width: '100%'}}>
              <div style={{flex: '1', width: '50%', marginLeft: '5px'}}>
                 {renderTabs()}
              </div>

              <Grid
                sx={{
                  flex: 1,
                  width: '50%',
                  justifyContent: 'center',
                  display: 'flex',
                  marginTop: '64px'
                }}>
                 <Card
                  sx={{
                    p: 3,
                    boxShadow: 5,
                    borderRadius: '8px',
                    flex: 1,
                    width: '50%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                  {isLoading ? (
                    <PulseLoader size={12} color={theme.palette.primary.main} />
                  ) : (
                    <GrowthChartComponent data={chartData} />
                  )}
                </Card>
              </Grid>
            </div>
            )
          ) : (
            <CircularProgressDiv height="50vh">
              <p>Please select a Legacy App and Growth Chart type to view data.</p>
            </CircularProgressDiv>
          )}
        </>
      )}
    </div>
  );
};

export default GrowthChartWrapper;
